import framingAssets from './framingAssets'
import { calculateFramingDimensions } from './framingDimensions'

// shortcut: use an illustrative 1/4 inch float gap until production gap measurements are available.
const FLOAT_CANVAS_PREVIEW_GAP = 0.25

const normalizedName = (name) =>
  String(name || '')
    .trim()
    .replace(/&/g, ' and ')
    .replace(/\s+/g, ' ')
    .toUpperCase()
const selected = (input) =>
  input?.selectedValue !== undefined &&
  input.selectedValue !== null &&
  input.selectedValue !== '' &&
  input.selectedValue !== 'default'

export const parseInchMeasurement = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : NaN
  if (typeof value !== 'string') return NaN
  const match = value.trim().match(/^(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)$|^(\d*\.?\d+)$/)
  if (!match) return NaN
  return match[4] !== undefined
    ? Number(match[4])
    : Number(match[1] || 0) + Number(match[2]) / Number(match[3])
}

const parseProfileWidth = (text) => {
  const match = String(text || '').match(
    /(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)\s*(?:"|inch(?:es)?)\s*Profile/i,
  )
  return match ? parseInchMeasurement(match[1]) : undefined
}

const matBorderFromLabel = (text) => {
  if (/^(?:none|no[\s-]*mat)$/i.test(String(text).trim())) return 0
  const match = String(text || '').match(
    /^(\d+\s+\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?)\s*(?:"|inch(?:es)?)\s*mat$/i,
  )
  return match ? parseInchMeasurement(match[1]) : NaN
}

export const resolveFrameAsset = (
  { name, family, widthInches, supplierCode },
  assets = framingAssets,
) => {
  const namedWidth = String(name || '').match(/^(.+?)\s+(\d+(?:\.\d+)?)"$/)
  const lookupName = namedWidth ? namedWidth[1] : name
  const width = widthInches ?? (namedWidth ? Number(namedWidth[2]) : undefined)
  if (namedWidth && widthInches !== undefined && Number(namedWidth[2]) !== widthInches) return null
  const eligible = assets.filter(
    (asset) =>
      asset.family === family &&
      (width === undefined || asset.widthInches === width) &&
      (!supplierCode || asset.supplierCode === supplierCode),
  )
  const exactMatches = eligible.filter((asset) =>
    [asset.name, ...(asset.aliases || [])].some(
      (label) => normalizedName(label) === normalizedName(name),
    ))
  const matches =
    exactMatches.length > 0
      ? exactMatches
      : eligible.filter((asset) => normalizedName(asset.name) === normalizedName(lookupName))
  // Multiple supplier finishes with the same name cannot be safely guessed.
  return matches.length === 1 ? matches[0] : null
}

const resolveServiceFrame = (frameType, frameName, stainName, attrs) => {
  const isFloatCanvas = frameType === 'Hardwood Float Canvas Frame'
  const family = frameType === 'Hardwood Frame' || isFloatCanvas ? 'hardwood' : 'contemporary'
  let profileWidth
  if (attrs.frameProfileSize !== undefined && attrs.frameProfileSize !== null) {
    profileWidth = parseInchMeasurement(attrs.frameProfileSize)
  } else if (family === 'hardwood') {
    profileWidth = parseProfileWidth(frameName) ?? parseProfileWidth(attrs.name)
  }
  let assetName = isFloatCanvas ? frameName.split(' - ')[0] : attrs.frameWoodPricing
  if (family === 'hardwood') {
    assetName = normalizedName(stainName).endsWith(` ${normalizedName(assetName)}`)
      ? stainName
      : `${stainName} ${assetName}`
  }
  const asset = resolveFrameAsset({ name: assetName, family, widthInches: isFloatCanvas ? 0.75 : profileWidth })
  // Float profiles reuse the wood grain; their physical width comes from the selected service.
  return isFloatCanvas && asset ? { ...asset, widthInches: profileWidth } : asset
}

export const buildMultiOpeningFramingConfig = ({ form, selectedProduct, finishingServicesData }) => {
  const inputs = form?.inputs || {}
  if (!selectedProduct || inputs.finishingType?.selectedValue !== 'framed') return null
  const frameType = inputs.frameType?.selectedValueText
  const isFloatCanvas = frameType === 'Hardwood Float Canvas Frame'
  if (!selected(inputs.frameType) || !selected(inputs.frameName) || inputs.frameName.isLoading || inputs.frameName.isDisabled) {
    return { status: 'waiting', message: 'Select a frame to see its preview.' }
  }
  if (!['Contemporary Frame', 'Hardwood Frame', 'Hardwood Float Canvas Frame'].includes(frameType)) {
    return { status: 'unavailable', message: 'A preview is not available for this frame construction.' }
  }
  if ((frameType === 'Hardwood Frame' || isFloatCanvas) && !selected(inputs.frameStain)) {
    return { status: 'waiting', message: 'Select a frame stain to see its finish.' }
  }
  const frameName = inputs.frameName.selectedValueText
  const matches = (finishingServicesData?.data || []).filter(({ id, attributes: attrs = {} }) =>
    attrs.finishingCategoryGuid === form.meta?.finishingCategoryId &&
    (isFloatCanvas
      ? id === inputs.frameName.selectedValue || attrs.guid === inputs.frameName.selectedValue
      : (frameType === 'Hardwood Frame' ? attrs.frameWoodAndProfileName : attrs.frameWoodPricing) === frameName),
  )
  const service = matches.find((row) => row.id === form.meta?.finishingServiceId || row.id === inputs.frameName.selectedValue) || matches[0]
  if (!service) return { status: 'waiting', message: 'Loading the selected frame configuration…' }
  const frame = resolveServiceFrame(frameType, frameName, inputs.frameStain?.selectedValueText, service.attributes)
  if (!frame || !(frame.widthInches > 0) || !frame.assetUrl) {
    return { status: 'unavailable', message: 'Preview unavailable: this frame finish needs a matching texture with verified measurements.' }
  }
  try {
    const dimensions = calculateFramingDimensions({
      printWidth: selectedProduct.matWidth,
      printHeight: selectedProduct.matHeight,
      matBorders: isFloatCanvas ? FLOAT_CANVAS_PREVIEW_GAP : 0,
      frameWidth: frame.widthInches,
    })
    return {
      status: 'ready', frame, dimensions,
      matColor: isFloatCanvas ? '#303030' : '#ffffff',
      ...(isFloatCanvas && { construction: 'float-canvas', floatGapInches: FLOAT_CANVAS_PREVIEW_GAP }),
    }
  } catch (error) {
    return { status: 'unavailable', message: error.message }
  }
}

const validateProductionMatSize = (trueMatSize, printSize, dimensions) => {
  if (!trueMatSize) return
  const trueMat = String(trueMatSize).match(/^\((\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)\)$/i)
  if (!trueMat) {
    throw new Error('The production mat dimensions are not available in a supported format.')
  }
  const { printWidth, printHeight, matWidth, matHeight } = dimensions
  const option = printSize.options?.find((row) => row.value === printSize.selectedValue)
  const rotated =
    option &&
    parseInchMeasurement(option.xAspectRatio) === printHeight &&
    printWidth !== printHeight
  const trueWidth = Number(trueMat[rotated ? 2 : 1])
  const trueHeight = Number(trueMat[rotated ? 1 : 2])
  // The service's true_mat_size is the overall mount size, not its opening.
  if (Math.abs(trueWidth - matWidth) > 0.0001 || Math.abs(trueHeight - matHeight) > 0.0001) {
    throw new Error(
      'This mount uses different production dimensions and cannot be previewed accurately yet.',
    )
  }
}

const getArtworkCrop = (meta, selectAllPhotos) => {
  if (selectAllPhotos) return null
  const values = [meta.cropPositionX, meta.cropPositionY, meta.cropWidth, meta.cropHeight]
  if (values.every((value) => value === undefined || value === null)) return null
  if (!values.every((value) => typeof value === 'number' && Number.isFinite(value))) {
    throw new Error('The photograph crop is not ready or contains invalid measurements.')
  }
  const [x, y, width, height] = values
  if (x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 100.01 || y + height > 100.01) {
    throw new Error('Adjust the photograph crop to fit inside the print.')
  }
  return { x, y, width, height }
}

// Reads the existing Zustand form and JSON:API services without changing the
// selection cascade, pricing, or saved line-item contract.
export const buildFramingConfig = ({
  form,
  finishingServicesData,
  selectedPhoto,
  selectAllPhotos,
}) => {
  const inputs = form?.inputs || {}
  const meta = form?.meta || {}
  const waiting = (message) => ({ status: 'waiting', message })
  const unavailable = (message) => ({ status: 'unavailable', message })
  if (!selected(inputs.printSize)) return waiting('Select a print size to see its framing preview.')
  const frameType = inputs.frameType?.selectedValueText
  const isFloatCanvas = frameType === 'Hardwood Float Canvas Frame'
  const isCanvas = isFloatCanvas || /canvas/i.test(inputs.paperType?.selectedValueText || '')
  if (!selected(inputs.frameType) || !selected(inputs.frame)) return waiting('Select a frame to see its preview.')
  if (!['Contemporary Frame', 'Hardwood Frame', 'Desktop Frame', 'Hardwood Float Canvas Frame'].includes(frameType)) {
    return unavailable('A preview is not available for this frame construction.')
  }
  if (inputs.frame.isLoading || inputs.frame.isDisabled || inputs.printSize.isLoading) return waiting('Loading frame options…')
  if (!isFloatCanvas && !selected(inputs.frameMat)) return waiting('Select a frame mat to see the complete preview.')
  if ((frameType === 'Hardwood Frame' || isFloatCanvas) && !selected(inputs.frameStain)) return waiting('Select a frame stain to see its finish.')
  if (!isFloatCanvas && meta.matOpeningShape && meta.matOpeningShape !== 'standard') {
    return unavailable('This preview currently supports rectangular mat openings.')
  }
  if (!isFloatCanvas && meta.matType === 'no-front-mat') return unavailable('A preview is not available for this mat construction.')
  if (/circle|oval|deckl/i.test(inputs.paperType?.selectedValueText || '')) {
    return unavailable('A preview is not available for this print construction.')
  }

  const frameName = inputs.frame.selectedValueText
  const matName = inputs.frameMat?.selectedValueText
  const matches = (finishingServicesData?.data || []).filter(
    ({ id, attributes: attrs = {} }) =>
      attrs.finishingCategoryGuid === meta.finishingCategoryId &&
      (isFloatCanvas
        ? id === inputs.frame.selectedValue || attrs.guid === inputs.frame.selectedValue
        : (frameType === 'Hardwood Frame' ? attrs.frameWoodAndProfileName : attrs.frameWoodPricing) === frameName) &&
      (isFloatCanvas || attrs.frameMatName === matName || (matName === 'None' && !attrs.frameMatName)),
  )
  const service =
    matches.find(
      (row) =>
        row.id === meta.finishingServiceId || row.attributes?.guid === meta.finishingServiceId,
    ) || matches[0]
  if (!service) return waiting('Loading the selected frame configuration…')
  const attrs = service.attributes
  const asset = resolveServiceFrame(frameType, frameName, inputs.frameStain?.selectedValueText, attrs)
  if (!asset) {
    return unavailable(
      'Preview unavailable: this frame finish needs a matching texture with verified measurements.',
    )
  }
  if (!(asset.widthInches > 0) || !Number.isFinite(asset.widthInches) || !asset.assetUrl) {
    return unavailable('Preview unavailable: the frame texture or width is invalid.')
  }

  try {
    const printWidth = parseInchMeasurement(meta.xAspectRatio)
    const printHeight = parseInchMeasurement(meta.yAspectRatio)
    const matBorders = isFloatCanvas ? FLOAT_CANVAS_PREVIEW_GAP : matBorderFromLabel(matName)
    if (!Number.isFinite(matBorders)) throw new Error('The mat border measurement is unavailable.')
    const dimensions = calculateFramingDimensions({
      printWidth,
      printHeight,
      matBorders,
      frameWidth: asset.widthInches,
    })
    if (!isFloatCanvas) validateProductionMatSize(attrs.trueMatSize, inputs.printSize, dimensions)
    const crop = getArtworkCrop(meta, selectAllPhotos)
    return {
      status: 'ready',
      dimensions,
      ...(isCanvas && { construction: isFloatCanvas ? 'float-canvas' : 'canvas' }),
      ...(isFloatCanvas && { floatGapInches: matBorders }),
      frame: { ...asset, id: service.id, name: frameName },
      artwork: {
        url: selectedPhoto?.attributes?.presignedPhotoUrl || null,
        name: selectedPhoto?.attributes?.photoFileName || 'Selected photograph',
        crop,
      },
      matColor: isFloatCanvas ? '#303030' : '#ffffff',
      batch: Boolean(selectAllPhotos),
    }
  } catch (error) {
    return unavailable(error.message)
  }
}
